import { ChakraProps, useToast } from '@chakra-ui/react';
import { useCreateGlobalCategoryMutation, useUpdateClientCategoryMutation } from 'core/API/globalCategoriesApi';
import { useTranslation } from 'helpers/languages/translations';
import { useEffect, useRef, useState } from 'react';
import { TCategoriesIdsByCompany, TClientCategoryItem, TGlobalCategoryRequest } from 'types';
import { TGlobalCategoriesHooks } from '../../useGlobalCategories';

interface IProps {
    dataToSubmit: TGlobalCategoriesHooks['dataToSubmit'];
    setDataToSubmit: TGlobalCategoriesHooks['setDataToSubmit'];
    setIsModalOpen: TGlobalCategoriesHooks['setIsModalOpen'];
    modalType: TGlobalCategoriesHooks['modalType'];
    availableCategories: TGlobalCategoriesHooks['availableCategories'];
    setAvailableCategories: TGlobalCategoriesHooks['setAvailableCategories'];
    deleteGlobalCategory: TGlobalCategoriesHooks['deleteGlobalCategory'];
    isDeleteLoading: TGlobalCategoriesHooks['isDeleteLoading'];
}

const useModal = ({
    dataToSubmit,
    setDataToSubmit,
    setIsModalOpen,
    modalType,
    availableCategories,
    setAvailableCategories,
    deleteGlobalCategory,
}: IProps) => {
    const [addGlobalCategory, { isLoading: isAddGlobalCategoryLoading }] = useCreateGlobalCategoryMutation();
    const [updateGlobalCategory, { isLoading: isUpdateGlobalCategoryLoading }] = useUpdateClientCategoryMutation();

    const [filteredAvailableCategories, setFilteredAvailableCategories] = useState<TClientCategoryItem[]>([]);
    const [filteredToSubmitCategories, setFilteredToSubmitCategories] = useState<TClientCategoryItem[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [toSubmitSearchValue, setToSubmitSearchValue] = useState<string>('');
    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);
    const toast = useToast();
    const isSubmitDisabled =
        isAddGlobalCategoryLoading ||
        isUpdateGlobalCategoryLoading ||
        !dataToSubmit.level_name_1?.trim() ||
        !dataToSubmit.level_name_2?.trim() ||
        !dataToSubmit.level_name_3?.trim() ||
        !dataToSubmit.client_categories.length;

    const handleCloseModal = () => {
        setDataToSubmit({
            client_categories: [],
            level_name_1: '',
            level_name_2: '',
            level_name_3: '',
        });
        setSearchValue('');
        setToSubmitSearchValue('');
        setIsModalOpen(false);
    };

    useEffect(() => {
        handleSearchChange(searchValue);
    }, [availableCategories]);

    useEffect(() => {
        handleToSubmitSearchChange(toSubmitSearchValue);
    }, [dataToSubmit.client_categories]);

    const generateCategoryItemProps = (category: TClientCategoryItem): Omit<ChakraProps, never> => {
        const hoverStyle: { [key: string]: string | number } = {};
        if (!category.selected) {
            hoverStyle['bg'] = 'gray.50';
        }
        return {
            borderBottom: '1px solid',
            borderColor: 'borderGray',
            p: '5px 15px',
            fontSize: 'sm',
            cursor: 'pointer',
            _hover: { ...hoverStyle },
            backgroundColor: category.selected ? '#368bff' : 'white',
            color: category.selected ? 'white' : 'black',
        };
    };

    const handleSelectCategory = (category: TClientCategoryItem, listType: 'available' | 'to_submit') => {
        const modifiedCategories = [
            ...(listType === 'available' ? availableCategories : dataToSubmit.client_categories),
        ];
        const categorieIndex = modifiedCategories.findIndex((c) => c.id === category.id);
        if (categorieIndex !== -1) {
            modifiedCategories[categorieIndex] = {
                ...modifiedCategories[categorieIndex],
                selected: !modifiedCategories[categorieIndex].selected,
            };
            if (listType === 'available') {
                setAvailableCategories(modifiedCategories);
            } else {
                setDataToSubmit((prev) => ({
                    ...prev,
                    client_categories: modifiedCategories,
                }));
            }
        }
    };

    const handleSelectAllCategories = (listType: 'available' | 'to_submit', action: 'select' | 'deselect') => {
        if (listType === 'available') {
            setAvailableCategories((prev) =>
                prev.map((category) => ({
                    ...category,
                    selected:
                        !searchValue || filteredAvailableCategories.find((c) => c.id === category.id)
                            ? action === 'select'
                            : category.selected,
                })),
            );
        } else {
            setDataToSubmit((prev) => ({
                ...prev,
                client_categories: prev.client_categories.map((category: TClientCategoryItem) => ({
                    ...category,
                    selected:
                        !toSubmitSearchValue || filteredToSubmitCategories.find((c) => c.id === category.id)
                            ? action === 'select'
                            : category.selected,
                })),
            }));
        }
    };

    const moveCategories = (direction: 'left' | 'right') => {
        if (
            (direction === 'right' && availableCategories.length) ||
            (direction === 'left' && dataToSubmit.client_categories.length)
        ) {
            const categoriesToMove: TClientCategoryItem[] = [];
            const categoriesToKeep: TClientCategoryItem[] = [];
            const categoriesToProcess = direction === 'right' ? availableCategories : dataToSubmit.client_categories;

            categoriesToProcess.forEach((category) => {
                if (category.selected) {
                    category.selected = false;
                    categoriesToMove.push(category);
                } else categoriesToKeep.push(category);
            });

            if (direction === 'right') {
                setAvailableCategories(categoriesToKeep);
                setDataToSubmit((prev) => ({
                    ...prev,
                    client_categories: [...prev.client_categories, ...categoriesToMove],
                }));
            }

            if (direction === 'left') {
                setDataToSubmit((prev) => ({
                    ...prev,
                    client_categories: categoriesToKeep,
                }));
                setAvailableCategories([...availableCategories, ...categoriesToMove]);
            }
        }
    };

    const handleCategoryNameChange = (value: string, level: 'level_name_1' | 'level_name_2' | 'level_name_3') => {
        setDataToSubmit((prev) => ({
            ...prev,
            [level]: value,
        }));
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        const lowerCaseValue = value.toLowerCase();
        if (!value) {
            setFilteredAvailableCategories(availableCategories);
        } else {
            const filteredCategories = availableCategories.filter((category) =>
                ` ${category.company_name} ${category.level_name_1} ${category.level_name_2} ${category.level_name_3}`
                    .toLowerCase()
                    .includes(lowerCaseValue),
            );
            setFilteredAvailableCategories(filteredCategories);
        }
    };

    const handleToSubmitSearchChange = (value: string) => {
        setToSubmitSearchValue(value);
        const lowerCaseValue = value.toLowerCase();
        if (!value) {
            setFilteredToSubmitCategories(dataToSubmit.client_categories);
        } else {
            const filteredCategories = dataToSubmit.client_categories.filter((category) =>
                ` ${category.company_name} ${category.level_name_1} ${category.level_name_2} ${category.level_name_3}`
                    .toLowerCase()
                    .includes(lowerCaseValue),
            );
            setFilteredToSubmitCategories(filteredCategories);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitDisabled) return;

        let resp = {};
        try {
            const client_categories = dataToSubmit.client_categories.reduce(
                (acc: TCategoriesIdsByCompany, currentCat: TClientCategoryItem) => {
                    if (acc[currentCat.company_id]) {
                        acc[currentCat.company_id].level_3_ids.push(currentCat.level_3);
                    } else {
                        acc[currentCat.company_id] = {
                            company_id: currentCat.company_id,
                            level_3_ids: [currentCat.level_3],
                        };
                    }
                    return acc;
                },
                {},
            );

            const formattedData = {
                national_client_categories: Object.values(client_categories),
                level_name_1: dataToSubmit.level_name_1,
                level_name_2: dataToSubmit.level_name_2,
                level_name_3: dataToSubmit.level_name_3,
            } as TGlobalCategoryRequest;

            if (modalType === 'add') {
                resp = await addGlobalCategory(formattedData);
            } else {
                resp = await updateGlobalCategory({ data: formattedData, globalCategoryId: dataToSubmit.id as number });
            }

            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('global_categories.toast.success.update'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }

        handleCloseModal();
    };

    const handleDeleteGlobalCategory = async () => {
        try {
            const resp = await deleteGlobalCategory([dataToSubmit.id as number]);

            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('global_categories.toast.success.delete'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
        handleCloseModal();
    };

    return {
        setIsModalOpen,
        isAddGlobalCategoryLoading,
        handleCloseModal,
        handleSubmit,
        handleDeleteGlobalCategory,
        updateInitialRef,
        deleteInitialRef,
        generateCategoryItemProps,
        moveCategories,
        handleSelectCategory,
        handleCategoryNameChange,
        handleSelectAllCategories,
        isUpdateGlobalCategoryLoading,
        filteredAvailableCategories,
        filteredToSubmitCategories,
        searchValue,
        toSubmitSearchValue,
        handleSearchChange,
        handleToSubmitSearchChange,
        isSubmitDisabled,
    };
};

export default useModal;
export type TModalHooks = ReturnType<typeof useModal>;
